import { useEffect, useState } from 'react'
import Button from 'components/form/Button'
import Icon from 'components/Icon'
import Table from 'components/table/Table'
import TableCheckBox from 'components/table/TableCheckBox'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearSelected,
  fetchPredictKeywords,
  fetchRegisteredKeywords,
  reset,
  selectAll,
  setPrice,
  setSelected,
} from 'features/keywordPlanner/predictKeywordSlice'
import PredictKeywordItem from './PredictKeywordItem'
import { useDialog } from 'features/dialog/Dialog'
import CurrencyInput from 'components/form/CurrencyInput'
import { downloadCsvFile, invalidNumberInput, isPresent } from 'utils'
import { fail } from 'features/toast/Toast'
import AddKeywordsModal from 'features/keywordPlanner/AddKeywordsModal'
import { fetchProposalKeywords } from 'features/keywordPlanner/proposalKeywordSlice'

const PredictKeyword = () => {
  const dispatch = useDispatch()
  const { keywords, price, isLoading, isRegisteredLoading, selected, content } =
    useSelector(state => state.keywordPlanner.predictKeyword)
  const [priceValue, setPriceValue] = useState(price)
  const [priceError, setPriceError] = useState(false)
  const [CopyKeywords, showCopyKeywords, hideCopyKeywords] = useDialog()
  const allChecked =
    selected.length > 0 && keywords.every(keyword => selected.includes(keyword))

  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch]
  )

  useEffect(() => {
    setPriceError(
      priceValue === '' || invalidNumberInput(priceValue, 100000, 70, 10)
    )
  }, [priceValue])

  useEffect(() => {
    if (keywords.length > 0) {
      dispatch(fetchPredictKeywords())
      dispatch(fetchRegisteredKeywords())
    }
  }, [dispatch, keywords])

  const handleChackAll = checked => {
    if (checked) {
      dispatch(selectAll())
    } else {
      dispatch(clearSelected())
    }
  }

  const handlePriceChange = ({ target: { value } }) => {
    setPriceValue(value)
  }

  const handleSubmit = event => {
    event.preventDefault()
    if (priceError) {
      fail('입찰가는 최소 70원 ~ 최대 100,000원까지 10원 단위로 설정해주세요.')
    } else {
      dispatch(setPrice(priceValue))
      dispatch(fetchPredictKeywords())
    }
  }

  const handleDownload = () => {
    const defaultStat = {
      bidRequest: '',
      click: '',
      bidAmount: '',
      rank: '',
    }
    const column = [
      '키워드',
      '모바일 예상광고요청수',
      '모바일 예상클릭수',
      '모바일 예상월비용',
      '모바일 예상순위평균',
      'PC 예상광고요청수',
      'PC 예상클릭수',
      'PC 예상월비용',
      'PC 예상순위평균',
    ].join(',')
    const row = keywords
      .map(kwd => {
        const stats = content.find(item => item.keyword === kwd)?.data
        const mobile = stats?.MOBILE || defaultStat
        const pc = stats?.PC || defaultStat
        return [
          kwd,
          mobile.bidRequest,
          mobile.click,
          mobile.bidAmount,
          mobile.rank,
          pc.bidRequest,
          pc.click,
          pc.bidAmount,
          pc.rank,
        ].join(',')
      })
      .join('\n')
    downloadCsvFile(`${column}\n${row}`, '키워드플래너_예상실적.csv')
  }

  const showProposalKeywords = () => {
    dispatch(reset())
    dispatch(fetchProposalKeywords())
  }

  const updateRegisteredKeywords = () => {
    dispatch(fetchRegisteredKeywords())
  }

  const handleDeselect = v => {
    dispatch(setSelected(v))
  }

  return (
    <>
      <div className="ad_managebox">
        <div className="managebox_tit">
          <strong className="tit_box">예상실적</strong>
          <div className="f_right">
            <Button line icon disabled={isLoading} onClick={handleDownload}>
              <Icon name="download">다운로드</Icon>
            </Button>
          </div>
        </div>
        <div className="managebox_tit">
          <div className="f_left">
            <form noValidate onSubmit={handleSubmit}>
              <fieldset>
                <div className="single_wrap">
                  <span className="txt_label">입찰가</span>
                  <CurrencyInput
                    min={70}
                    step={10}
                    value={priceValue}
                    onChange={handlePriceChange}
                    error={priceError}
                  />
                </div>
                <div className="single_wrap">
                  <Button
                    primary
                    type="submit"
                    disabled={!isPresent(priceValue)}>
                    조회
                  </Button>
                </div>
              </fieldset>
            </form>
          </div>
          <div className="f_right">
            <div className="single_wrap">
              <Button primary onClick={showProposalKeywords}>
                키워드 리스트로 돌아가기
              </Button>
            </div>
            <div className="single_wrap">
              <Button
                line
                onClick={showCopyKeywords}
                disabled={selected.length === 0}>
                키워드 바로등록
              </Button>
            </div>
          </div>
        </div>
        <div className="tblg2_wrap tblg_target">
          <div className="item_head">
            <Table className="tbl_g2" caption="키워드플래너 상단안내 표">
              <colgroup>
                <col width={57} />
                <col width={245} />
                <col width={109} />
                <col width={109} />
                <col width={109} />
                <col width={111} />
                <col width={109} />
                <col width={109} />
                <col width={109} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan="2" scope="col" className="align_c">
                    <div className="inner_tbl">
                      <TableCheckBox
                        checked={allChecked}
                        onCheckedChange={handleChackAll}
                      />
                    </div>
                  </th>
                  <th rowSpan="2" scope="col">
                    <div className="inner_tbl">키워드</div>
                  </th>
                  <th colSpan="4" scope="colgroup" className="align_c">
                    <div className="inner_tbl">
                      모바일
                      <HelpTooltip>
                        매체유형이 검색매체 &gt; 카카오이며, 모바일에서 발생된
                        최근30일간의 데이터 기준입니다.
                      </HelpTooltip>
                    </div>
                  </th>
                  <th colSpan="4" scope="colgroup" className="align_c">
                    <div className="inner_tbl">
                      PC
                      <HelpTooltip>
                        매체유형이 검색매체 &gt; 카카오이며, PC에서 발생된
                        최근30일간의 데이터 기준입니다.
                      </HelpTooltip>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상광고요청수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상클릭수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상월비용</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상순위평균</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상광고요청수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상클릭수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상월비용</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">예상순위평균</div>
                  </th>
                </tr>
              </thead>
            </Table>
          </div>
          <div className="item_body">
            <Table
              className="tbl_g2"
              caption="키워드플래너 전체 표"
              loading={isLoading || isRegisteredLoading}
              emptyMessage={
                <>
                  조회결과가 없습니다.
                  <br />
                  상단에서 키워드를 조회하세요.
                </>
              }>
              <Table.Head noHead>
                <Table.Column width={57} />
                <Table.Column width={245} />
                <Table.Column width={109} />
                <Table.Column width={109} />
                <Table.Column width={109} />
                <Table.Column width={111} />
                <Table.Column width={109} />
                <Table.Column width={109} />
                <Table.Column width={109} />
                <Table.Column />
              </Table.Head>
              <Table.Body>
                {keywords.map(keyword => (
                  <PredictKeywordItem key={keyword} keyword={keyword} />
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
        <div className="item_info">
          <ul className="list_dot">
            <li>
              제안된 키워드는 심사승인을 보장하지 않으며, 키워드 사용에 따른
              모든 법적 책임은 광고주에게 있습니다.
            </li>
            <li>
              제공되는 데이터는 프리미엄링크 캠페인 &gt; 검색매체 &gt;
              카카오지면을 통해 광고가 집행된 키워드의 최근30일(어제로부터 과거
              30일까지) 과거 실적 및 과거 실적을 토대로 예측한 수치입니다.
              <br />
              따라서, 제공되는 데이터로 인한 광고 성과를 보장하지 않으니,
              참고용으로만 사용하시기 바랍니다.
            </li>
          </ul>
        </div>
      </div>
      <CopyKeywords type="basic_layer" className="copy_layer">
        <AddKeywordsModal
          selectedKeywords={selected}
          afterConfirm={updateRegisteredKeywords}
          handleDeselect={handleDeselect}
          handleClose={hideCopyKeywords}
        />
      </CopyKeywords>
    </>
  )
}
export default PredictKeyword
